import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AppleLogo, GooglePlayLogo } from "./StoreIcons";

const ROTATING_WORDS = ["TRAVEL", "EXPENSE", "SETTLEMENT", "QONTRI"];

const SCREEN_LEFT =
  "https://customer-assets.emergentagent.com/job_25687ba4-de6b-41cb-ba59-a8223df26ff0/artifacts/xvl7fc0p_WhatsApp%20Image%202026-05-02%20at%2001.34.55%20%282%29.jpeg";
const SCREEN_CENTER =
  "https://customer-assets.emergentagent.com/job_25687ba4-de6b-41cb-ba59-a8223df26ff0/artifacts/dv3fu4en_WhatsApp%20Image%202026-05-02%20at%2001.34.51%20%281%29.jpeg";
const SCREEN_RIGHT =
  "https://customer-assets.emergentagent.com/job_25687ba4-de6b-41cb-ba59-a8223df26ff0/artifacts/xuqj8dc2_WhatsApp%20Image%202026-05-02%20at%2001.34.54%20%283%29.jpeg";

export default function Hero() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setIdx((i) => (i + 1) % ROTATING_WORDS.length),
      1900
    );
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden grain"
    >
      {/* Decorative gradient blobs */}
      <div
        aria-hidden
        className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-[#3056F5]/10 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#0F2C5A]/10 blur-3xl"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Kicker */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          data-testid="hero-kicker"
          className="text-center text-[#3056F5] font-bold tracking-[0.35em] text-xs md:text-sm mb-8"
        >
          HELLO, I AM QONTRI APP
        </motion.p>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          data-testid="hero-headline"
          className="font-display text-center text-[#0F2C5A] uppercase leading-[0.95] text-[clamp(2.6rem,9vw,7.5rem)]"
        >
          <span className="block">YOUR</span>
          <span
            data-testid="hero-rotating-text"
            className="relative inline-flex h-[1em] overflow-hidden align-baseline px-2 md:px-4 min-w-[6ch] md:min-w-[10ch] justify-center"
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={ROTATING_WORDS[idx]}
                initial={{ y: "100%", opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: "-100%", opacity: 0 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="text-[#3056F5] inline-block"
              >
                {ROTATING_WORDS[idx]}
              </motion.span>
            </AnimatePresence>
          </span>
          <span className="block">BUDDY</span>
        </motion.h1>

        {/* Sub-line */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mt-8 text-center text-[#14254B]/80 text-base md:text-lg max-w-xl mx-auto"
        >
          Smart contributions.{" "}
          <span className="text-[#3056F5] font-semibold">
            Simple settlements.
          </span>{" "}
          Split expenses with your travel crew — without the awkward math.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          id="download"
          data-testid="download-badges"
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href="#"
            data-testid="cta-appstore"
            className="group inline-flex items-center gap-3 bg-[#0F2C5A] hover:bg-[#14254B] text-white pl-5 pr-7 py-3.5 rounded-2xl transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#0F2C5A]/30"
          >
            <AppleLogo className="w-7 h-7" />
            <span className="text-left leading-none">
              <span className="block text-[10px] tracking-widest opacity-70">
                DOWNLOAD ON THE
              </span>
              <span className="block font-display text-xl tracking-tight">
                App Store
              </span>
            </span>
          </a>
          <a
            href="#"
            data-testid="cta-googleplay"
            className="group inline-flex items-center gap-3 bg-[#3056F5] hover:bg-[#2546D4] text-white pl-5 pr-7 py-3.5 rounded-2xl transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#3056F5]/40"
          >
            <GooglePlayLogo className="w-6 h-6" />
            <span className="text-left leading-none">
              <span className="block text-[10px] tracking-widest opacity-80">
                GET IT ON
              </span>
              <span className="block font-display text-xl tracking-tight">
                Google Play
              </span>
            </span>
          </a>
        </motion.div>

        {/* Phone mockups */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-mockups"
          className="mt-16 md:mt-24 relative flex items-end justify-center gap-4 md:gap-10"
        >
          <PhoneFrame
            src={SCREEN_LEFT}
            tilt={-6}
            delay={0.85}
            className="hidden sm:block w-[180px] md:w-[260px] translate-y-6"
          />
          <PhoneFrame
            src={SCREEN_CENTER}
            tilt={0}
            delay={0.95}
            featured
            className="w-[230px] md:w-[320px] -translate-y-2"
          />
          <PhoneFrame
            src={SCREEN_RIGHT}
            tilt={6}
            delay={1.05}
            className="hidden sm:block w-[180px] md:w-[260px] translate-y-6"
          />
        </motion.div>
      </div>
    </section>
  );
}

function PhoneFrame({
  src,
  tilt = 0,
  delay = 0,
  className = "",
  featured = false,
  fit = "cover",
  bgColor = "#fff",
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40, rotate: tilt - 4 }}
      animate={{ opacity: 1, y: 0, rotate: tilt }}
      transition={{ delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -8, rotate: tilt * 0.5 }}
      className={`relative ${className}`}
    >
      <div
        className={`relative rounded-[2.2rem] md:rounded-[2.6rem] p-1.5 bg-[#0F2C5A] shadow-2xl ${
          featured ? "shadow-[#3056F5]/30" : "shadow-[#0F2C5A]/20"
        }`}
      >
        <div
          className="overflow-hidden rounded-[1.7rem] md:rounded-[2.1rem] aspect-[9/19]"
          style={{ background: bgColor }}
        >
          <img
            src={src}
            alt="Qontri app screen"
            className={`w-full h-full ${
              fit === "contain" ? "object-contain" : "object-cover"
            }`}
            loading="lazy"
          />
        </div>
        {/* Notch */}
        <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-20 h-5 bg-[#0F2C5A] rounded-full" />
      </div>
    </motion.div>
  );
}
